import React from "react";
import {
  getOtherOptionValue,
  isOtherOptionValue,
} from "./otherFieldHelpers";

const fieldClass =
  "w-full px-4 py-3 border-0 rounded-lg text-sm text-gray-700 bg-[#F3F5F9] focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white placeholder-gray-400 transition disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

const normalizeOptions = (options = []) =>
  options.map((option) => (typeof option === "string" ? { value: option, label: option } : option));

export function FormField({ label, required, hint, children }) {
  return (
    <div className="mb-4">
      <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder = "Enter text",
  type = "text",
  className = "",
  ...props
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${fieldClass} ${className}`.trim()}
      {...props}
    />
  );
}

export function SelectInput({
  value,
  onChange,
  options,
  placeholder = "Select Item",
  className = "",
  ...props
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className={`${fieldClass} appearance-none cursor-pointer ${className}`.trim()}
        {...props}
      >
        <option value="">{placeholder}</option>
        {normalizeOptions(options).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function SelectInputWithOther({
  label,
  required,
  hint,
  value,
  otherValue = "",
  onChange,
  onOtherChange,
  options,
  placeholder = "Select Item",
  otherPlaceholder = "Please specify",
  className = "",
  otherClassName = "",
  ...props
}) {
  const normalized = normalizeOptions(options);
  const showOther = isOtherOptionValue(value, normalized);
  const otherOptionValue = getOtherOptionValue(normalized);

  return (
    <FormField label={label} required={required} hint={hint}>
      <div className="space-y-2">
        <SelectInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={options}
          placeholder={placeholder}
          className={className}
          {...props}
        />
        {showOther && (
          <TextInput
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            placeholder={otherPlaceholder || `Specify ${otherOptionValue}`}
            className={otherClassName}
          />
        )}
      </div>
    </FormField>
  );
}

export function FreeTextArea({ value, onChange, placeholder, className = "", ...props }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={3}
      className={`${fieldClass} mt-2 resize-none ${className}`.trim()}
      {...props}
    />
  );
}

export function MultiToggle({ values = [], onChange, options = [] }) {
  const normalized = normalizeOptions(options);
  const toggle = (value) =>
    onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);

  return (
    <div className="flex flex-wrap gap-2">
      {normalized.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => toggle(option.value)}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-all ${
            values.includes(option.value)
              ? "bg-green-50 text-green-700 border-green-400"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Dropdown checklist for option objects { value, label } — lets a user pick
// more than one item from a searchable-looking closed list, with selected
// items shown as removable chips underneath. Used by Vet Certificate's
// "Species / Commodity Covered" and Livestock Actor's "Species Specialisation".
export function MultiSelectPicklist({ values = [], onChange, options = [], placeholder = 'Select options' }) {
  const [open, setOpen] = React.useState(false);
  const normalized = normalizeOptions(options);
  const toggle = (val) => onChange(values.includes(val) ? values.filter((v) => v !== val) : [...values, val]);
  const selectedLabels = normalized.filter((o) => values.includes(o.value));

  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }} tabIndex={-1}>
      <div onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 rounded-lg text-sm bg-[#F3F5F9] cursor-pointer flex justify-between items-center hover:bg-gray-200 transition">
        <span className={values.length ? 'text-gray-700' : 'text-gray-400'}>
          {values.length ? `${values.length} selected` : placeholder}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
          {normalized.length === 0 && <p className="px-4 py-3 text-[13px] text-gray-400">No options available</p>}
          {normalized.map((opt) => {
            const selected = values.includes(opt.value);
            return (
              <div key={opt.value} onMouseDown={(e) => { e.preventDefault(); toggle(opt.value); }}
                className={`px-4 py-2.5 text-[13px] cursor-pointer flex items-center gap-2.5 transition ${selected ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition ${selected ? 'bg-green-600 border-green-600' : 'border-gray-300'}`}>
                  {selected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <polyline points="4 12 9 17 20 7" />
                    </svg>
                  )}
                </div>
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
      {selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedLabels.map((opt) => (
            <span key={opt.value} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-[12px] font-medium">
              {opt.label}
              <button type="button" onMouseDown={(e) => { e.preventDefault(); toggle(opt.value); }} className="ml-0.5 text-green-400 hover:text-green-700 transition">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Restricts input to digits only, capped at 11 (Nigerian phone number length).
// Wraps the onChange event so callers keep using the same event.target.value contract.
export function PhoneInput({ value, onChange, placeholder = 'e.g. 08012345678', className = '', maxDigits = 11, ...props }) {
  const handleChange = (e) => {
    const digits = String(e.target.value ?? '').replace(/\D/g, '').slice(0, maxDigits);
    onChange({ target: { value: digits } });
  };
  return (
    <input
      type="tel"
      inputMode="numeric"
      value={value ?? ''}
      onChange={handleChange}
      placeholder={placeholder}
      maxLength={maxDigits}
      className={`${fieldClass} ${className}`.trim()}
      {...props}
    />
  );
}

export function SingleToggle({ value, onChange, options = [] }) {
  const normalized = normalizeOptions(options);
  return (
    <div className="flex flex-wrap gap-2">
      {normalized.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(value === option.value ? "" : option.value)}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-all ${
            value === option.value
              ? "bg-green-50 text-green-700 border-green-400"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

